import { ArrayUtility } from '../../engine/utils';
import { EmbeddedPeerConnection } from '../embedded/embedded-peer-connection';
import { EmbeddedSignalingChannel } from '../embedded/embedded-signaling-channel';
import { SignalRSignalingChannel } from '../signalr/signalr-signaling-channel';
import { WebRTCCheck } from '../webrtc/web-rtc-check';
import { PreviewRtcGuestConnectionFactory } from './preview-rtc-guest-connection-factory';
/**
 * Our role for debug messages.
 */
const role = 'PreviewHost';
export class PreviewHostImp {
    constructor(signalingUrl, previewSession, metaData, instanceId) {
        this.previewSession = previewSession;
        this.metaData = metaData;
        this.instanceId = instanceId;
        /**
         * Contains all WebRTC signaling channels, per scope.
         */
        this.signalingChannels = new Map();
        /**
         * Contains, per scope, a connection to send control messages (such as highlighting elements in the preview) over.
         * Note that we could do the same for signalingConnections, where only the Application scope uses SignalR.
         */
        this.controlConnections = new Map();
        /**
         * Bundles we keep for clients that connect later.
         */
        this.bundles = new Map();
        /**
         * Control messages for clients that connect later.
         */
        this.controlMessages = new Map();
        this.bundleHashes = new Map();
        this.previewEventListeners = new Set();
        /**
         * Contains, per scope, the last message that could not be delivered because no guest was connected (yet).
         */
        this.undeliveredMessages = new Map();
        const isWebRTCSupported = WebRTCCheck.checkWebRTCSupported();
        this.rtcConnectionFactory = new PreviewRtcGuestConnectionFactory(previewSession.iceServers, this, role);
        if (isWebRTCSupported) {
            const appSignalingChannel = this.createRemoteSignalingChannel(signalingUrl, previewSession, instanceId);
            this.signalingChannels.set(1 /* PreviewScope.Application */, appSignalingChannel);
        }
        this.sessionId = previewSession.sessionId;
        this.isSupported = isWebRTCSupported;
    }
    createRemoteSignalingChannel(signalingUrl, previewSession, instanceId) {
        // Host should always support WebRTC and not use Signaling for previews (even though we can).
        // In other words: just don't run a designer session on iOS.
        const supportsDataChannels = true;
        return new SignalRSignalingChannel(this, this.rtcConnectionFactory, signalingUrl, previewSession.sessionId, // We use the sessionId as the SignalR group name
        /* isInitiator:*/ true, 0 /* PreviewScope.None */, // this is the PreviewScope of the host, which it doesn't have
        supportsDataChannels, role, instanceId);
    }
    createEmbeddedSignalingChannel(connection, previewSession, instanceId) {
        const supportsDataChannels = true;
        return new EmbeddedSignalingChannel(this, this.rtcConnectionFactory, connection, previewSession.sessionId, // We use the sessionId as the SignalR group name
        /* isInitiator:*/ true, 0 /* PreviewScope.None */, // this is the PreviewScope of the host, which it doesn't have
        supportsDataChannels, role, instanceId);
    }
    //#region SignalingChannelEventListener implementation
    onChannelReady(channel) {
        console.log(`${role}: a signaling channel (${channel.type}) is ready. Requesting to join group...`);
        channel
            .joinGroup()
            .then((response) => {
            if (!response.success) {
                return;
            }
            console.log(`${role}: successfully joined group '${response.groupName} (${channel.type})'. Client ID: ${response.clientId}.`);
        }).catch((reason) => {
            console.error(`${role}: error while logging in:`, reason);
        });
    }
    /**
     * Called when a peer has joined the group successfully.
     * As a response, because we are the host, we make a connection with the peer.
     * This will be followed by a handlePeerConnected call below, with isInitiator set to true.
     */
    onPeerJoined(channel, message) {
        // const otherClientId = message.clientId;
        if (message.isHost) {
            // User has opened the same project (= host) in another window. Never connect to another host, it makes no sense.
            return console.warn(`Another host has joined session ${this.sessionId}. This might result in project conflicts.`);
        }
        // console.log(`${role}: setting up connection with peer ${otherClientId}. CanUseWebRTC: ${message.canUseWebRTC}.`);
        channel.connectToPeer(message.clientId, message.scope, message.instanceId, message.canUseWebRTC);
    }
    /**
     * A peer has connected directly to us.
     * @param connection The peer connection.
     * @param isInitiator True if the host is the initiator of the connection, false if the
     * event is caused by a connection request from the peer (typically if the peer was already
     * in the group and the host connected later).
     */
    onPeerConnectionCreated(connection, isInitiator) {
        // console.log(`PreviewHost: created peer connection with ${connection.peerId}. Initiator: ${isInitiator}.`);
        const newConnection = connection;
        newConnection.init().then(() => {
            const scope = newConnection.scope;
            console.log(`${role}: created a connection with peer ${connection.peerId}. Scope: ${scope}`);
            // If we already have a generated bundle (which we normally already do), send it.
            const bundleWithProperties = this.bundles.get(scope);
            if (bundleWithProperties) {
                this.sendBundle(bundleWithProperties, scope, newConnection);
            }
            const undelivered = this.undeliveredMessages.get(scope);
            if (undelivered) {
                console.log(`Sending undelivered '${undelivered.type}' message to new guest with scope ${scope || 'all'}.`);
                try {
                    newConnection.sendMessage(undelivered);
                    this.undeliveredMessages.delete(scope); // todo: we might want to keep some undelivered messages for other clients for some scopes
                }
                catch (e) {
                    console.error(e);
                }
            }
        });
    }
    //#endregion SignalingChannelEventListener implementation
    leave() {
        for (const channel of this.signalingChannels.values()) {
            channel.dispose();
        }
    }
    changeSession(sessionId) {
        const promises = [];
        for (const channel of this.signalingChannels.values()) {
            promises.push(channel.changeGroup(sessionId));
        }
        return Promise.allSettled(promises).then(() => {
            console.log(`${role}: changed session ${this.sessionId} -> ${sessionId}`);
            this.sessionId = sessionId;
        });
    }
    reload(url, scope) {
        const message = { type: 'reload-window', url: url };
        this.sendMessageToGuests(message, scope);
    }
    clearPreview(scope) {
        // Remove the bundle from memory, making sure that the preview is not reloaded when a new guest connects.
        if (!this.bundles.delete(scope)) {
            return;
        }
        const pauseControlMessage = { type: 2 /* ControlMessageType.PauseControl */ };
        this.sendControlMessage(pauseControlMessage, scope, true);
        // Note: in addition, we could send a message to guests to reload themselves, or unload their previews too, like:
        // const msg: ClearPreviewMessage = { type: 'clear-preview', message: message };
        // this.sendMessageToGuests(scope, msg);
    }
    replaceBunde(bundle, properties, scope) {
        const bundleWithProperties = {
            bundle: bundle,
            properties: properties
        };
        this.bundles.set(scope, { bundle: bundle, properties: properties });
        return bundleWithProperties;
    }
    setAppPreview(bundle, properties, sendToGuests) {
        const bundleWithProperties = this.replaceBunde(bundle, properties, 1 /* PreviewScope.Application */);
        if (sendToGuests) {
            this.sendBundle(bundleWithProperties, 1 /* PreviewScope.Application */);
        }
    }
    setWidgetPreview(bundle, properties) {
        const bundleWithProperties = this.replaceBunde(bundle, properties, 3 /* PreviewScope.Widget */);
        this.sendBundle(bundleWithProperties, 3 /* PreviewScope.Widget */);
    }
    setComponentPreview(bundle, properties) {
        const bundleWithProperties = this.replaceBunde(bundle, properties, 2 /* PreviewScope.Component */);
        this.sendBundle(bundleWithProperties, 2 /* PreviewScope.Component */);
    }
    changeProjectName(projectName) {
        const message = { type: 'set-project-name', name: projectName };
        this.saveProjectName(projectName);
        this.sendMessageToGuests(message, 1 /* PreviewScope.Application */);
    }
    sendBundle(bundleWithProperties, scope, onlyToGuest) {
        const bundle = bundleWithProperties.bundle;
        const dirtyModuleCount = ArrayUtility.count(bundle.modulesMeta, m => m.isDirty);
        const totalSize = bundle.modules.reduce((prev, blob) => prev + blob.size, 0);
        const message = {
            type: 'has-preview',
            properties: bundleWithProperties.properties,
            moduleCount: bundle.modulesMeta.length,
            dirtyModuleCount: dirtyModuleCount,
            manifest: bundle.manifest,
            previousHash: this.bundleHashes.get(scope)
        };
        // Update the hash now we have retrieved the previous one
        this.bundleHashes.set(scope, bundle.manifest.hash);
        if (!this.metaData.supportsHmr) {
            message.reloadAll = true;
        }
        const guestConnections = onlyToGuest ? [onlyToGuest] : [...this.getActiveGuestConnections(scope)];
        const promises = guestConnections.map(guest => guest.sendPreviewBundle(message, bundle));
        Promise
            .all(promises)
            .then(() => {
            console.log(`${role}: preview bundle sent to ${promises.length} guests. App size: ${totalSize} bytes, ${message.moduleCount} modules (${message.dirtyModuleCount} dirty).`);
        }).catch((reason) => {
            console.error(reason);
        });
    }
    sendMessageToGuests(message, scope) {
        let i = 0;
        for (const connection of this.getActiveGuestConnections(scope)) {
            connection.sendMessage(message);
            i++;
        }
        if (i === 0 && scope != null) {
            this.undeliveredMessages.set(scope, message);
            console.log(`${role}: message '${message.type}' cannot be delivered yet because there are no guests with scope ${scope}.`);
        }
        else {
            console.log(`${role}: sent '${message.type}' message to ${i} guests in scope ${scope}.`);
        }
    }
    //#region embedded guests
    connectEmbeddedGuest(target, scope) {
        // Always disconnect first
        this.disconnectEmbeddedGuest(scope);
        const connection = new EmbeddedPeerConnection(target, scope);
        this.controlConnections.set(scope, connection);
        const lastControlMessage = this.controlMessages.get(scope);
        if (lastControlMessage) {
            // console.log(`Sending last control message to client with scope ${scope}.`, lastControlMessage);
            connection.sendControlMessage(lastControlMessage);
        }
        const signalingChannel = this.createEmbeddedSignalingChannel(connection, this.previewSession, this.instanceId);
        this.signalingChannels.set(scope, signalingChannel);
    }
    disconnectEmbeddedGuest(scope) {
        const signalingChannel = this.signalingChannels.get(scope);
        if (signalingChannel) {
            signalingChannel.dispose();
        }
        this.signalingChannels.delete(scope);
        this.controlConnections.delete(scope);
    }
    //#endregion embedded guests
    //#region control messages
    selectNodes(nodeIds, scope) {
        const message = { nodeIds: nodeIds, type: 1 /* ControlMessageType.SelectNodes */ };
        this.sendControlMessage(message, scope, false);
    }
    sendControlMessage(message, scope, onlyIfConnected) {
        const connection = this.controlConnections.get(scope);
        if (connection) {
            // console.log(`Sending control message to client with scope ${scope}.`, message);
            connection.sendControlMessage(message);
        }
        // else {
        //     console.log(`Unable to send message because there is no client connection with scope ${scope}.`, message);
        // }
        if (!onlyIfConnected) {
            // Remember the message in case the client connects later (we could make this optional per message).
            this.controlMessages.set(scope, message);
        }
    }
    //#endregion control messages
    //#region implementation of PreviewGuestMessageListener
    onReceivedGuestMessage(guest, message) {
        switch (message.type) {
            case 'preview-loaded':
                for (let l of this.previewEventListeners) {
                    l.onPreviewLoaded(guest.scope);
                }
                break;
        }
    }
    addEventListener(listener) {
        this.previewEventListeners.add(listener);
    }
    removeEventListener(listener) {
        this.previewEventListeners.delete(listener);
    }
    //#endregion implementation of PreviewGuestMessageListener
    /**
    * Updates the project name in the current application bundle so that it is passed correctly
    * when the guest reconnects.
    */
    saveProjectName(projectName) {
        const appBundle = this.bundles.get(1 /* PreviewScope.Application */);
        if (appBundle) {
            appBundle.properties.name = projectName;
        }
    }
    *getActiveGuestConnections(scope) {
        for (const [eachScope, eachChannel] of this.signalingChannels) {
            if ((scope != null && scope !== eachScope) || eachChannel.isDisposed) {
                continue;
            }
            const peers = eachChannel.getPeers();
            for (const clientId in peers) {
                const peer = peers[clientId];
                if (peer.isInitialized) {
                    yield peer;
                }
            }
        }
    }
}
//# sourceMappingURL=preview-host-imp.js.map